import cx from 'classnames';
import {isFunction} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Dropdown, Icon, Button, ActionButton} from 'components';
import {TRANSLATION_KEYS} from 'locale/translation-keys';

const T_KEYS = TRANSLATION_KEYS.COMMON;

type ChildrenProps = {
    isOpen: boolean;
    close: () => void;
};
export type FiltersFormChildrenProps = ChildrenProps;

type Props = {
    filtersDirty: boolean;
    filtersEqualDefault: boolean;
    clearFilters: () => void;
    applyFilters: () => void;
    className?: string;
    refreshClassName?: string;
    applyClassName?: string;
    isLoading?: boolean;
    children?: ((props: ChildrenProps) => React.ReactElement) | React.ReactElement;
};

export const FiltersDropdown: React.FC<Props> = ({
    className, refreshClassName, applyClassName, filtersDirty, filtersEqualDefault, isLoading, children,
    clearFilters, applyFilters
}: Props) => {
    const {t} = useTranslation();
    const [isDropDownOpen, setIsDropDownOpen] = useState(false);

    const closeDropdown = useCallback(() => {
        setIsDropDownOpen(false);
    }, []);

    const handleClearFilters = useCallback(() => {
        clearFilters();
        closeDropdown();
    }, [clearFilters, closeDropdown]);

    const handleApplyFilters = useCallback(() => {
        closeDropdown();
        applyFilters();
    }, [closeDropdown, applyFilters]);

    const childrenProps = useMemo(() => ({
        isOpen: isDropDownOpen,
        close: closeDropdown
    }), [isDropDownOpen, closeDropdown]);

    const filtersIconColor = useMemo(() => {
        if (filtersEqualDefault) {
            return 'default-dimmed';
        } else if (filtersDirty) {
            return 'primary-light';
        }
        return 'primary';
    }, [filtersDirty, filtersEqualDefault]);

    return (
        <div className={cx(className, 'tw-flex tw-items-center tw-flex-wrap')}>
            <Dropdown
                controlled
                hideOnClick={false}
                open={isDropDownOpen}
                triggerNode={
                    <Icon
                        name='filter'
                        button
                        size='lg'
                        color={filtersIconColor}
                    />
                }
                onOpen={() => setIsDropDownOpen(true)}
                onClose={closeDropdown}
            >
                <div className='tw-min-w-64 tw-p-4 tw-grid tw-gap-2 tw-text-left'>
                    <div className='tw-flex tw-justify-between tw-items-center'>
                        <div className='tw-ts-h5-bold'>{t(T_KEYS.ALL.FILTERS)}</div>
                        <Icon name='close' className='tw-mr-0' button onClick={closeDropdown} />
                    </div>
                    {isFunction(children) ? children(childrenProps) : children}
                    <div className='tw-grid tw-grid-cols-2 tw-gap-2 tw-mt-2'>
                        <ActionButton
                            loading={isLoading}
                            disabled={filtersEqualDefault}
                            width='small'
                            action='delete'
                            hideIcon
                            onClick={handleClearFilters}
                        >
                            {t(T_KEYS.BUTTONS.CLEAR)}
                        </ActionButton>
                        <Button
                            color='primary'
                            loading={isLoading}
                            width='small'
                            disabled={!filtersDirty}
                            onClick={handleApplyFilters}
                        >
                            {t(T_KEYS.BUTTONS.APPLY)}
                        </Button>
                    </div>
                </div>
            </Dropdown>
            {filtersDirty ?
                <Button
                    className={cx(applyClassName, 'tw-mx-3')}
                    color='primary'
                    width='small'
                    loading={isLoading}
                    onClick={handleApplyFilters}
                >
                    {t(T_KEYS.BUTTONS.APPLY)}
                </Button> :
                <Button
                    className={cx(refreshClassName, 'tw-mx-3')}
                    loading={isLoading}
                    icon='refresh'
                    width='small'
                    onClick={handleApplyFilters}
                >
                    {t(T_KEYS.BUTTONS.REFRESH)}
                </Button>}
        </div>
    );
};
